/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   CORE / MAIL-KIND-PREVIEW — Vorlagen-Auflösung + Testdaten-Vorschau
   =============================================================================
   S225 CUT A. Trennt die Default-/Preview-abhängigen Helfer vom reinen Katalog
   (mail-kinds.js), damit email-templates.js den Toggle-Resolver importieren kann
   OHNE Import-Zyklus über mail-template-defaults.js (das seinerseits die
   DEFAULT_*-Templates aus email-templates.js liest → TDZ-Crash beim Laden).

   Rein (kein DOM, kein window) → voll testbar. Nur dieses Modul kennt die
   effektiven Default-Texte (mail-template-defaults.js).
============================================================================ */

import { htmlEscape, fmtEUR } from '../utils/helpers.js';
import { getMailTemplateDefault } from './mail-template-defaults.js';
import { getMailKind } from './mail-kinds.js';

/** Effektiver Wert eines Feldes: getrimmter Override, sonst Default. */
function _effectiveField(overrideVal, defaultVal) {
  return (typeof overrideVal === 'string' && overrideVal.trim()) ? overrideVal : (defaultVal || '');
}

/**
 * Effektive Vorlage eines Kinds ({subject, body}) — Override aus dem Settings-
 * Slot, sonst der (auto-aktualisierbare) Default. Spiegelt die resolve*Template-
 * Präzedenz der Versand-Pipeline.
 */
export function resolveMailKindTemplate(settings, kind) {
  const meta = getMailKind(kind);
  if (!meta) return { subject: '', body: '' };
  const def = getMailTemplateDefault(meta.settingsKey, meta.settingsField) || { subject: '', body: '' };
  const slot = (settings && settings[meta.settingsKey]) || {};
  const ov = slot[meta.settingsField] || {};
  return {
    subject: _effectiveField(ov.subject, def.subject),
    body:    _effectiveField(ov.body,    def.body),
  };
}

/**
 * „angepasst": Override existiert UND weicht vom aktuellen Default ab. Ein
 * gespeicherter Override, der exakt dem Default entspricht (Alt-Daten), zählt
 * NICHT (identisch zur alten renderTemplateKindEditor-Logik).
 */
export function isMailKindOverridden(settings, kind) {
  const meta = getMailKind(kind);
  if (!meta) return false;
  const def = getMailTemplateDefault(meta.settingsKey, meta.settingsField) || { subject: '', body: '' };
  const slot = (settings && settings[meta.settingsKey]) || {};
  const ov = slot[meta.settingsField] || {};
  const subjOverridden = !!(ov.subject && ov.subject.trim() && ov.subject !== def.subject);
  const bodyOverridden = !!(ov.body && ov.body.trim() && ov.body !== def.body);
  return subjOverridden || bodyOverridden;
}

/**
 * Positions-Block der Vorschau aus dem ECHTEN Menü des Tenants (state.menu) —
 * keine erfundenen Gerichte (Jonas 2026-07-08: „nur echte Daten", z. B. Hauspommes
 * statt „Pommes frites"). Nimmt die ersten verfügbaren Menü-Items (stabil nach
 * displayOrder/Name), Mengen 2×/1×, und rechnet die Summe daraus. Fallback nur,
 * wenn KEIN Menü geladen ist (Tests / frischer Tenant).
 */
function _realItemsFixture(menu) {
  const list = (Array.isArray(menu) ? menu : [])
    .filter(m => m && typeof m.name === 'string' && m.name.trim() && m.available !== false);
  list.sort((a, b) =>
    (Number(a.displayOrder ?? 999) - Number(b.displayOrder ?? 999)) ||
    String(a.name).localeCompare(String(b.name)));
  const pick = list.slice(0, 2);
  if (!pick.length) {
    return { block: '• 1x (kein Menü hinterlegt)', total: fmtEUR(0) };
  }
  const qtys = [2, 1];
  let sumCents = 0;
  const lines = pick.map((m, i) => {
    const q = qtys[i] || 1;
    const priceEur = Number(m.price) || 0;
    sumCents += Math.round(priceEur * 100) * q;
    return `• ${q}x ${m.name} (${fmtEUR(priceEur)})`;
  });
  return { block: lines.join('\n'), total: fmtEUR(sumCents / 100) };
}

/**
 * Testdaten-Fixture für die Vorschau. Restaurant-spezifische Werte kommen aus
 * dem ECHTEN System: `items`/`items_block`/`total` aus `menu` (state.menu),
 * `brand`/`phone` aus den Settings. Nicht-restaurant-spezifische Felder
 * (Gast-Name, Datum, Order-/Beleg-Nummer) sind neutrale Muster-Werte — bewusst
 * KEINE echten Kundendaten (kein PII in der Vorschau).
 *
 * @param {object} settings  state.settings (brand/name/phone)
 * @param {Array}  [menu]    state.menu — echte Menü-Items für den Positions-Block
 */
export function mailPreviewFixture(settings, menu) {
  const s = settings || {};
  const items = _realItemsFixture(menu);
  return {
    name: 'Max Mustermann',
    order_number: 'GG-2026-0042',
    invoice_number: 'RG-2026-0042',
    payment_method: 'EC / Kreditkarte',
    pickup_date: '07.07.2026',
    pickup_time: '18:30',
    reservation_date: '07.07.2026',
    reservation_time: '19:00',
    guests: '4',
    total: items.total,
    gross: '1.234,50 €',
    count: '37',
    zbon_number: 'Z-2026-014',
    date: '07.07.2026',
    area: 'Innenbereich (Tisch 4)',
    brand: (s.restaurantName || 'Grill & Green'),
    phone: (s.restaurantPhone || ''),
    tracking_link: 'https://grill-green.app/#track=demo',
    cancel_link: 'https://grill-green.app/#cancel=demo',
    // S226 — Newsletter-DOI: neutraler Demo-Bestätigungslink für die Vorschau
    // (echt gefüllt zur Sendezeit via Edge buildConfirmLink aus dem confirm_token).
    confirm_link: 'https://grill-green.app/#newsletter-confirm/demo',
    // S262 — Dienstplan: neutrale Muster-Woche + Demo-Link (echt zur Sendezeit
    // via Edge buildScheduleLink / weekRangeDe / deadlineDe).
    week_no: '39',
    week_range: 'Mo 21.09. – So 27.09.2026',
    week_start: 'Mo., 21.09.2026',
    deadline: 'So, 20.09., 12:00 Uhr',
    schedule_link: 'https://grill-green.app/#dienstplan/demo',
    // S264 — Plan-Mail: neutrale Muster-Woche (keine echten Namen).
    version: '2',
    my_shifts: '• Mo 21.09.: 12–16\n• Do 24.09.: 12–22',
    plan: 'Mo 21.09.: ★ 12–16 Max, Mia · 16–22 Ali\nDi 22.09.: 12–22 Mia\nMi 23.09.: —\nDo 24.09.: ★ 12–22 Max\nFr 25.09.: 12–16 Ali · 16–22 Mia, Max\nSa 26.09.: 12–22 Ali\nSo 27.09.: —',
    items: items.block,
    items_block: items.block,
  };
}

/**
 * S226-HF2 (#4) — Spiegel von Edge smoothEmptyName(): Betreff-Reste nach leerer
 * {{name}}-Substitution glätten (Theke-/Tisch-Platzhalter). Konservativ, für
 * Betreffzeilen. Rein + testbar.
 */
export function smoothEmptyName(s) {
  if (typeof s !== 'string') return s;
  return s
    .replace(/\s+([,.!?;:])/g, '$1')
    .replace(/,\s*([.!?])/g, '$1')
    .replace(/([,;:])\1+/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .replace(/^[\s,;:]+/, '')
    .trim();
}

/** Ersetzt alle {{token}} durch Fixture-Werte (unbekannte Tokens bleiben leer).
 *  Rein — kein DOM, kein window. */
export function fillMailPreview(text, fixture) {
  if (typeof text !== 'string' || !text) return '';
  const vars = fixture || {};
  return text.replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (_m, tok) => {
    const v = vars[tok];
    return v == null ? '' : String(v);
  });
}

/**
 * Baut das selbst-enthaltene Vorschau-HTML für das sandboxed iframe (srcdoc):
 * helle Papier-Insel im dunklen Modal-Rahmen (gg-darkmode-invariants). Betreff +
 * Body mit Testdaten gefüllt. Rein + testbar.
 */
export function buildMailKindPreviewHtml(settings, kind, menu) {
  const tpl = resolveMailKindTemplate(settings, kind);
  const fixture = mailPreviewFixture(settings, menu);
  const subject = htmlEscape(smoothEmptyName(fillMailPreview(tpl.subject, fixture)));
  const body = htmlEscape(fillMailPreview(tpl.body, fixture));
  return `<!doctype html><html lang="de"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>
  :root { color-scheme: light; }
  html,body { margin:0; }
  body { background:#efe9dd; padding:22px 16px; font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif; }
  .paper { max-width:560px; margin:0 auto; background:#ffffff; border-radius:14px;
           padding:26px 30px; box-shadow:0 6px 24px rgba(0,0,0,.14); color:#1c1c1c; }
  .label { text-transform:uppercase; letter-spacing:.06em; font-size:.66rem; color:#9a8f76; margin:0 0 10px; }
  .subject { font-weight:700; font-size:1.08rem; line-height:1.35; margin:0 0 16px;
             padding-bottom:14px; border-bottom:1px solid #eee2cf; color:#141414; }
  .body { white-space:pre-wrap; line-height:1.6; font-size:.95rem; color:#2a2a2a; }
</style></head><body>
  <div class="paper">
    <p class="label">Vorschau · Testdaten</p>
    <div class="subject">${subject || '(kein Betreff)'}</div>
    <div class="body">${body || '(kein Text)'}</div>
  </div>
</body></html>`;
}
